/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Thu Jun 6 2019 3:16:54 PM
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Page, Layout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { Xmas22Constants } from 'src/constants/campaigns';
import { allowUsingNewSearchSettings } from 'src/helpers/search-settings-helper';

import {
  getDisplayBlocks,
  updateBlocksWithBFCM2020,
  updateBlocksWithSpecialDiscount,
  allowToShowSpecialDiscount
} from 'src/helpers/dashboard-helper';
import MainMenu from 'src/components/containers/main-menu';
import WelcomeCustom from './welcomeCustom';
// import Welcome1 from './welcome1';
// import Welcome2 from './welcome2';
import WelcomeOnboarding from './welcomeOnboarding';
import WelcomeAppEngagement from './welcomeAppEngagement';
import WelcomeOldLowPlus from './welcomeOldLowPlus';
import WelcomeOldHighPlus from './welcomeOldHighPlus';
import OnboardingChecklist from './onboardingChecklist';
import OnboardingArticles from './onboardingArticles';
import Notifications from './notifications';
import EuropeanLanguageNotification from './europeanLanguageNotification';
import TopNotifications from './topNotifications';
import ThemeSetupStatus from './themeSetupStatus';
import SubscriptionStatus from './subscriptionStatus';
import MenuDashboard1 from './menuDashboard1';
import MenuDashboardAppEngagement from './menuDashboardAppEngagement';
import SyncHookStatus from './syncHookStatus';
import GettingStartedVideos from './gettingStartedVideos';
import GuideToKnowledgeBase from './guideToKnowledgeBase';
import GuideToKnowledgeBaseAppEngagement from './guideToKnowledgeBaseAppEngagement';
import RecommendedApps from './recommendedApps';
import Review from 'src/components/views/dashboard/review';
import ExpReviewRating from './experiment/expReviewRating';
import HelpCenter from './helpCenter';
import Feedback from './feedback';
import OptionInEmail from './optionInEmail';
import WelcomeNewFeature from 'src/components/containers/common/welcome-new-feature-modal';
import PreLaunch0423Popup from 'src/components/containers/common/pre-launch-0423-popup';
import SpecialDiscountWelcomeModal from 'src/components/views/campaigns/bfcm2022/special-discount-welcome-modal';
import SpecialDiscountBlockNotification from 'src/components/views/campaigns/bfcm2022/special-discount-block-notification';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import * as dashboardAction from 'actions/dashboard-actions';
import moment from 'moment';

type Props = {
  dashboardBlocks: Array<string>,
  getDashboardBlocks: () => void,
  getDashboardComponents: (language: string) => void
};
type ComposedProps = Props & WithI18nProps;
/**
 * Render associated component based on name
 *
 * @param  {string} name component name
 *
 * @return {object}      react component
 */
const renderComponent = name => {
  const components = {
    topNotifications: <TopNotifications blockName="topNotifications" />,
    welcome1: <WelcomeCustom blockName="welcome" />,
    specialDiscount: <SpecialDiscountBlockNotification />,
    // welcome2: <Welcome2 blockName="welcome2" />,
    welcomeOnboarding: <WelcomeOnboarding blockName="welcomeOnboarding" />,
    welcomeAppEngagement: <WelcomeAppEngagement blockName="welcomeAppEngagement" />,
    welcomeOldLowPlus: <WelcomeOldLowPlus blockName="welcomeOldLowPlus" />,
    welcomeOldHighPlus: <WelcomeOldHighPlus blockName="welcomeOldHighPlus" />,
    onboardingChecklist: <OnboardingChecklist blockName="onboardingChecklist" />,
    onboardingArticles: <OnboardingArticles blockName="onboardingArticles" />,
    europeanLanguageNotification: (
      <EuropeanLanguageNotification blockName="europeanLanguageNotification" />
    ),
    notifications: <Notifications blockName="notifications" />,
    themeSetupStatus: <ThemeSetupStatus blockName="themeSetupStatus" />,
    subscriptionStatus: <SubscriptionStatus blockName="subscriptionStatus" />,
    optionInEmail: <OptionInEmail blockName="optionInEmail" />,
    menuDashboard1: <MenuDashboard1 blockName="menuDashboard1" />,
    menuDashboardAppEngagement: (
      <MenuDashboardAppEngagement blockName="menuDashboardAppEngagement" />
    ),
    syncHookStatus: <SyncHookStatus blockName="syncHookStatus" />,
    gettingStartedVideos: <GettingStartedVideos blockName="gettingStartedVideos" />,
    guideToKnowledgeBase: <GuideToKnowledgeBase blockName="guideToKnowledgeBase" />,
    guideToKnowledgeBaseAppEngagement: (
      <GuideToKnowledgeBaseAppEngagement blockName="guideToKnowledgeBaseAppEngagement" />
    ),
    recommendedApps: <RecommendedApps blockName="recommendedApps" />,
    review: <Review blockName="review" />,
    expReviewRating: <ExpReviewRating blockName="expReviewRating" />,
    helpCenter: <HelpCenter blockName="helpCenter" />,
    feedback: <Feedback blockName="feedback" />
  };

  return components[name] || '';
};

class Dashboard extends Component<ComposedProps> {
  state = {
    activeWelcomeMerchandising: false,
    activePreLaunchModal: false,
    showSpecialDiscountModal: false
  };

  handleDismissShowSpecialDiscount = () => {
    this.setState({ showSpecialDiscountModal: false });
  };

  async componentDidMount() {
    const {
      language,
      getDashboardBlocks,
      getDashboardComponents,
      tenantApp,
      allowRecommendation,
      allowMerchandisingV2,
      allowSearchV2
    } = this.props;
    // Render shopify menu bar
    // buttonTitleBarSAB('Dashboard');
    // const releaseDay = Math.round(new Date('2023-04-18') / 1000);

    // Get list of blocks to be displayed
    getDashboardBlocks();
    getDashboardComponents(language);

    // Display welcome modal after 3s
    setTimeout(() => {
      if (allowMerchandisingV2 || allowRecommendation || allowSearchV2) {
        this.setState({ activeWelcomeMerchandising: false });
      } else {
        this.setState({ activePreLaunchModal: true });
      }
    }, 3000);

    setTimeout(() => {
      const isAllowSpecialDiscount = allowToShowSpecialDiscount(
        tenantApp,
        Xmas22Constants.startDate,
        Xmas22Constants.endDate
      );

      try {
        const isNotShowSpecialDiscountAgain1Day = localStorage.getItem(
          `${tenantApp?.tenantId}-isNotShowSpecialDiscountAgain1Day`
        );
        let countShowPopup = localStorage.getItem(`${tenantApp?.tenantId}-countShowPopup`) || 0;
        const countShowPopupDate = localStorage.getItem(
          `${tenantApp?.tenantId}-countShowPopupDate`
        );
        if (!countShowPopupDate) {
          localStorage.setItem(
            `${tenantApp?.tenantId}-countShowPopupDate`,
            moment().endOf('day').toISOString()
          );
        } else {
          if (countShowPopupDate !== moment().endOf('day').toISOString()) {
            localStorage.setItem(
              `${tenantApp?.tenantId}-countShowPopupDate`,
              moment().endOf('day').toISOString()
            );
            localStorage.setItem(`${tenantApp?.tenantId}-countShowPopup`, 1);
            countShowPopup = 0;
          }
        }
        const dismissXmas22 = localStorage.getItem(`${tenantApp?.tenantId}-dismissXmas22`);

        if (
          isAllowSpecialDiscount &&
          !isNotShowSpecialDiscountAgain1Day &&
          !dismissXmas22 &&
          countShowPopup < 5
        ) {
          this.setState({ showSpecialDiscountModal: true });
          localStorage.setItem(`${tenantApp?.tenantId}-countShowPopup`, +countShowPopup + 1);
        }
      } catch (error) {
        if (isAllowSpecialDiscount) {
          this.setState({ showSpecialDiscountModal: true });
        }
      }
    }, 1500);
  }

  // Render the row based on the number of component in row
  renderRow(row) {
    const secondary = row.length === 2 ? true : false;
    const oneThird = row.length === 3 ? true : false;

    return (
      <React.Fragment>
        {row.map((column, index) => {
          return (
            <Layout.Section secondary={secondary && index > 0} oneThird={oneThird} key={column}>
              {renderComponent(column)}
            </Layout.Section>
          );
        })}
      </React.Fragment>
    );
  }

  render() {
    const { dashboardBlocks, tenantApp, allowRecommendation, allowMerchandisingV2, allowSearchV2 } =
      this.props;
    const { activePreLaunchModal } = this.state;
    const today = new Date();

    const showPreLaunch0423Popup = today >= new Date('2023-04-10');

    // Determine blocks to be rendered
    let displayBlocks = getDisplayBlocks(dashboardBlocks, tenantApp);
    displayBlocks = updateBlocksWithBFCM2020(displayBlocks, tenantApp);
    displayBlocks = updateBlocksWithSpecialDiscount(displayBlocks, tenantApp);
    return (
      <React.Fragment>
        <MainMenu menuId="menu-home" homepage={true} />
        <div className="BC-AnotherLayout">
          <div className="BC-Page BC-Page__Dashboard">
            <Page>
              <Layout>
                {/* <Layout.Section>
                  <Locale />
                </Layout.Section> */}

                {displayBlocks.map((row, index) => {
                  return (
                    <React.Fragment key={'row-' + index}>{this.renderRow(row)}</React.Fragment>
                  );
                })}
              </Layout>
            </Page>
          </div>
        </div>

        <WelcomeNewFeature
          active={false}
          onClose={this.onCloseWelcomeMerchandisingModal}
          allowMerchandisingV2={allowMerchandisingV2}
          allowRecommendation={allowRecommendation}
          allowSearchV2={allowSearchV2}
        />

        {showPreLaunch0423Popup && (
          <PreLaunch0423Popup
            active={activePreLaunchModal}
            onClose={() => this.toggleModal('activePreLaunchModal')}
          />
        )}

        <SpecialDiscountWelcomeModal
          active={this.state.showSpecialDiscountModal}
          onClose={this.handleDismissShowSpecialDiscount}
        />
      </React.Fragment>
    );
  }

  onCloseWelcomeMerchandisingModal = () => {
    this.setState({ activeWelcomeMerchandising: false, activePreLaunchModal: true });
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const allowRecommendation = _.get(tenantApp, 'settings.recommendation.settings.enable', false);
  const allowMerchandisingV2 = _.get(tenantApp, 'adminAppFeatures.merchandisingV2', false);
  const searchVersion = _.get(tenantApp, 'parseParams.searchVersion', '1.0');

  const allowSearchV2 = allowUsingNewSearchSettings(searchVersion);

  return {
    dashboardBlocks: _.get(state.dashboard, 'dashboardBlocks', {}),
    tenantApp,
    allowRecommendation,
    allowMerchandisingV2,
    allowSearchV2,
    language: _.get(state.oauth, 'tenantApp.appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getDashboardBlocks: () => dispatch(dashboardAction.getDashboardBlocks(ownProps.i18n)),
  getDashboardComponents: language =>
    dispatch(dashboardAction.getDashboardComponents(language, ownProps.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Dashboard));
